// RepartoAseadorasField — selector de 1..N aseadoras + editor del reparto del
// sueldo. Round 2026-08-25. Lo comparten el modal de /aseos y el del
// calendario (CleaningFields) para que la regla viva UNA vez.
//
// Contrato con el Form padre (nombres de campos que este componente gobierna):
//   - `idAseadores`: number[] — el equipo, responsable primero (orden de
//     selección del multi-Select).
//   - `asignaciones`: [{usuario_id, monto}] — el reparto. Se mantiene en
//     sincronía: seleccionar/quitar gente re-parte en PARTES IGUALES (el
//     default del negocio); cambiar el sueldo total re-escala PROPORCIONAL
//     (un 70/30 sigue siendo 70/30); editar un monto a mano es libre y la
//     validación exige que la suma calce EXACTA con el total.
//   - `idAseador` + `nombreAseador`: espejos legacy (la responsable) que el
//     resto del form/payload sigue usando.
//
// El backend re-valida todo (services/asignacionesAseoWrite.js): esto es UX,
// no la frontera de seguridad.

import React, { useCallback, useEffect } from "react";
import { Alert, Button, Form, InputNumber, Select, Space, Typography } from "antd";
import type { FormInstance } from "antd";
import { TeamOutlined } from "@ant-design/icons";
// Formatter/parser CLP compartidos (review 2026-09-07: la copia inline no
// tenía el strip de ceros a la izquierda que clpInput.ts existe para arreglar).
import { clpInputFormatter, clpInputParser } from "../../../utils/clpInput";
import { AvisoAseosMismoDia } from "./AvisoAseosMismoDia";

/**
 * Campo "contenedor" para valores no-escalares dentro de un Form.Item hidden:
 * registra el name en el form sin renderizar nada (un `<Input hidden>` con un
 * array como value hace warning de React).
 *
 * OJO deploy: este const vivía ENTRE los imports y `import/first` (warning de
 * lint) se vuelve ERROR con CI=true — el build de Railway fallaba y el bundle
 * viejo seguía sirviéndose. Verificar siempre con `CI=true npm run build`.
 */
const HiddenField: React.FC<{ value?: unknown; onChange?: (v: unknown) => void }> = () => null;

export interface AseadoraOption {
  id: number;
  nombre: string;
}

export interface AsignacionForm {
  usuario_id: number;
  monto: number;
}

const clp = (v: number) => `$${Math.round(v).toLocaleString("es-CL")}`;

/** Partes iguales con suma EXACTA: el resto (total mod n) va a las primeras. */
export function repartirPartesIgualesUI(total: number, n: number): number[] {
  const t = Math.max(0, Math.trunc(Number(total) || 0));
  const partes = Math.max(1, Math.trunc(n) || 1);
  const base = Math.floor(t / partes);
  const resto = t - base * partes;
  return Array.from({ length: partes }, (_, i) => base + (i < resto ? 1 : 0));
}

/** Re-escala proporcional (resto mayor) conservando la suma exacta. */
export function reescalarProporcionalUI(
  asignaciones: AsignacionForm[],
  nuevoTotal: number,
): AsignacionForm[] {
  if (!asignaciones.length) return [];
  const t = Math.max(0, Math.trunc(Number(nuevoTotal) || 0));
  const totalViejo = asignaciones.reduce((acc, a) => acc + (Number(a.monto) || 0), 0);
  if (totalViejo <= 0) {
    const partes = repartirPartesIgualesUI(t, asignaciones.length);
    return asignaciones.map((a, i) => ({ usuario_id: a.usuario_id, monto: partes[i] }));
  }
  const exactos = asignaciones.map((a) => (t * (Number(a.monto) || 0)) / totalViejo);
  const pisos = exactos.map(Math.floor);
  let faltan = t - pisos.reduce((acc, v) => acc + v, 0);
  const orden = exactos
    .map((v, i) => ({ i, frac: v - Math.floor(v) }))
    .sort((a, b) => b.frac - a.frac || a.i - b.i);
  const montos = [...pisos];
  for (const { i } of orden) {
    if (faltan <= 0) break;
    montos[i] += 1;
    faltan -= 1;
  }
  return asignaciones.map((a, i) => ({ usuario_id: a.usuario_id, monto: montos[i] }));
}

interface Props {
  form: FormInstance;
  aseadoras: AseadoraOption[];
  /** Nombre del campo del form que lleva el sueldo TOTAL del aseo. */
  totalFieldName: string;
  disabled?: boolean;
  /**
   * Aviso «esta aseadora ya tiene otro aseo ese día» (round 2026-09-11): los
   * nombres de los campos de fecha del form padre y el aseo que se edita (no
   * cuenta como cruce consigo mismo). Sin esta prop no se avisa.
   */
  avisoMismoDia?: {
    campoDesde: string;
    campoHasta: string;
    excluirAseoId?: number | null;
  };
}

const RepartoAseadorasField: React.FC<Props> = ({
  form,
  aseadoras,
  totalFieldName,
  disabled,
  avisoMismoDia,
}) => {
  const asignaciones: AsignacionForm[] = Form.useWatch("asignaciones", form) ?? [];
  const totalRaw = Form.useWatch(totalFieldName, form);
  const total = Math.max(0, Math.trunc(Number(totalRaw) || 0));

  const nombreDe = useCallback(
    (id: number) => aseadoras.find((a) => a.id === id)?.nombre ?? `#${id}`,
    [aseadoras],
  );

  /** Escribe el reparto + los espejos legacy (idAseador/nombreAseador). */
  const escribir = useCallback(
    (lista: AsignacionForm[]) => {
      const primera = lista[0];
      form.setFieldsValue({
        asignaciones: lista,
        idAseadores: lista.map((a) => a.usuario_id),
        idAseador: primera ? primera.usuario_id : undefined,
        nombreAseador: primera ? nombreDe(primera.usuario_id) : undefined,
      });
    },
    [form, nombreDe],
  );

  // Cambió el TOTAL con equipo ya elegido → re-escala proporcional para que la
  // suma siga calzando (sin pisar un reparto custom).
  useEffect(() => {
    if (!asignaciones.length) return;
    const suma = asignaciones.reduce((acc, a) => acc + (Number(a.monto) || 0), 0);
    if (suma === total) return;
    escribir(reescalarProporcionalUI(asignaciones, total));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [total]);

  const onSeleccionChange = (ids: number[]) => {
    // Conservar el monto de quien ya estaba SOLO si el equipo no cambió de
    // tamaño-composición; al agregar/quitar gente el default es partes
    // iguales (regla del negocio; después se ajusta a mano si hace falta).
    const partes = repartirPartesIgualesUI(total, Math.max(ids.length, 1));
    escribir(ids.map((id, i) => ({ usuario_id: id, monto: partes[i] ?? 0 })));
    // Acción del ADMIN (no una hidratación ni un re-escalado derivado): el
    // payload del calendario solo manda `asignaciones` cuando esto es true —
    // así una edición que no tocó el equipo deja que el backend preserve el
    // reparto vigente (y lo re-escale él si cambió el sueldo).
    form.setFieldsValue({ repartoTocado: true });
  };

  const onMontoChange = (usuarioId: number, monto: number | null) => {
    escribir(
      asignaciones.map((a) =>
        a.usuario_id === usuarioId ? { ...a, monto: Math.trunc(Number(monto) || 0) } : a,
      ),
    );
    form.setFieldsValue({ repartoTocado: true });
  };

  const suma = asignaciones.reduce((acc, a) => acc + (Number(a.monto) || 0), 0);
  const descalce = asignaciones.length > 0 ? suma - total : 0;

  return (
    <>
      <Form.Item
        name="idAseadores"
        label={
          <span>
            <TeamOutlined /> Aseadoras asignadas
          </span>
        }
        tooltip="Puedes asignar el aseo a una o varias aseadoras. El pago total se reparte entre ellas — en partes iguales por defecto, o como tú definas."
        rules={[
          {
            validator: async (_rule, value: number[]) => {
              if (!Array.isArray(value) || value.length === 0) {
                throw new Error("Selecciona al menos una aseadora");
              }
            },
          },
        ]}
      >
        <Select
          mode="multiple"
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder="Selecciona una o varias aseadoras"
          disabled={disabled}
          onChange={onSeleccionChange}
          options={aseadoras.map((a) => ({ value: a.id, label: a.nombre }))}
        />
      </Form.Item>

      {/* Round 2026-09-11 — aviso en vivo si alguna ya tiene otro aseo esos días. */}
      {avisoMismoDia && (
        <AvisoAseosMismoDia
          form={form}
          usuarioIds={asignaciones.map((a) => a.usuario_id)}
          campoDesde={avisoMismoDia.campoDesde}
          campoHasta={avisoMismoDia.campoHasta}
          excluirAseoId={avisoMismoDia.excluirAseoId ?? null}
        />
      )}

      {/* El reparto vive en el form (hidden) para viajar con el submit. La
          validación de suma exacta corre acá para bloquear el guardado. */}
      <Form.Item
        name="asignaciones"
        hidden
        rules={[
          {
            validator: async (_rule, value: AsignacionForm[]) => {
              if (!Array.isArray(value) || value.length <= 0) return;
              const s = value.reduce((acc, a) => acc + (Number(a.monto) || 0), 0);
              if (s !== total) {
                throw new Error(
                  `El reparto suma ${clp(s)} y el pago del aseo es ${clp(total)} — deben calzar exacto`,
                );
              }
            },
          },
        ]}
      >
        <HiddenField />
      </Form.Item>
      <Form.Item name="repartoTocado" hidden>
        <HiddenField />
      </Form.Item>

      {asignaciones.length >= 2 && (
        <div
          style={{
            border: "1px solid #d9d9d9",
            borderRadius: 8,
            padding: "10px 12px",
            marginBottom: 16,
            background: "#fafafa",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 8,
            }}
          >
            <Typography.Text strong style={{ fontSize: 13 }}>
              Reparto del pago ({clp(total)})
            </Typography.Text>
            <Button
              size="small"
              disabled={disabled}
              onClick={() =>
                escribir(
                  asignaciones.map((a, i) => ({
                    usuario_id: a.usuario_id,
                    monto: repartirPartesIgualesUI(total, asignaciones.length)[i],
                  })),
                )
              }
            >
              Partes iguales
            </Button>
          </div>
          <Space direction="vertical" size={6} style={{ width: "100%" }}>
            {asignaciones.map((a, i) => (
              <div
                key={a.usuario_id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 8,
                }}
              >
                <span style={{ fontSize: 13 }}>
                  {nombreDe(a.usuario_id)}
                  {i === 0 && (
                    <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                      {" "}
                      (responsable)
                    </Typography.Text>
                  )}
                </span>
                <InputNumber
                  min={0}
                  step={1000}
                  value={a.monto}
                  disabled={disabled}
                  style={{ width: 140 }}
                  formatter={clpInputFormatter}
                  parser={clpInputParser}
                  onChange={(v) => onMontoChange(a.usuario_id, v as number | null)}
                />
              </div>
            ))}
          </Space>
          {descalce !== 0 && (
            <Alert
              type="warning"
              showIcon
              style={{ marginTop: 8 }}
              message={
                descalce > 0
                  ? `El reparto suma ${clp(Math.abs(descalce))} de MÁS que el pago del aseo`
                  : `Al reparto le faltan ${clp(Math.abs(descalce))} para calzar con el pago del aseo`
              }
              description='Ajusta los montos o usa "Partes iguales". No se puede guardar hasta que la suma calce exacta.'
            />
          )}
        </div>
      )}
    </>
  );
};

export default RepartoAseadorasField;
